// Client layer (TS-2.5.03) - BRD section 8.6. Edits the four layout caps this batch exposes a form
// for; every other LayoutSettings field (quarterPolicy, detailSelection, presenter, dateParsing,
// outputProfiles, resolvedQuarterColumns) round-trips unchanged from what was loaded, so saving the
// caps can never silently clobber a setting this screen does not render a control for.
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ApiError } from "../../api/ApiError";
import type { LayoutSettings } from "../../api/generated/contracts";
import { configurationApi } from "../../api/configuration";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { WarningsBanner } from "../../components/WarningsBanner";
import { issueBannerItems } from "./lib/adminBanners";
import { strings } from "../../strings";

const F = strings.admin.layout;
const FB = strings.admin.layout.boardColumns;

// The caps' explicit spinbutton range - a wide sanity bound, not business validation (the server
// stays the authority). Stated because a number input without min/max exposes valuemin/valuemax 0
// in Chromium's accessibility tree, which a screen-reader user hears as a 0..0 control.
const CAP_RANGE = { min: 1, max: 999 } as const;

export function LayoutSettingsPanel() {
  const [settings, setSettings] = useState<LayoutSettings | null>(null);
  const [concurrencyToken, setConcurrencyToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);
  const [banners, setBanners] = useState<ReturnType<typeof issueBannerItems>>([]);

  const refresh = useCallback(() => {
    setLoading(true);
    void configurationApi
      .getLayoutSettings()
      .then((next) => {
        setSettings(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "Layout settings could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(refresh, [refresh]);

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    setSaving(true);
    try {
      const result = await configurationApi.saveLayoutSettings({ settings, concurrencyToken });
      setBanners(issueBannerItems(result.issues));
      if (result.saved && result.entity) {
        setSettings(result.entity);
        setConcurrencyToken(result.concurrencyToken);
      }
    } finally {
      setSaving(false);
    }
  }

  function updateField(patch: Partial<Pick<LayoutSettings, "glanceRowCapWithStats" | "glanceRowCapPlain" | "riskDecisionItemsPerSlide" | "requestsPerSlide">>) {
    setSettings((current) => (current ? { ...current, ...patch } : current));
  }

  return (
    <section className="eg-admin-layout">
      <h2>{F.heading}</h2>

      {loading && !settings && (
        <div role="status" aria-live="polite">
          {F.loading}
        </div>
      )}

      {error && (
        <div role="alert">
          <p>{F.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      <WarningsBanner items={banners} />

      {settings && (
        <>
          <form className="eg-admin-layout__form" onSubmit={(event) => void handleSave(event)}>
            {/* The four caps are grouped by the slide family they control (fieldset/legend, so the
                grouping is announced, not just drawn). CAP_RANGE below: without an explicit min/max
                a number input's spinbutton exposes a degenerate 0..0 range in Chromium's
                accessibility tree, which reads as broken; the range here is a wide sanity bound,
                and the server remains the authority on what it accepts. */}
            <fieldset className="eg-admin__fieldset">
              <legend>{F.glanceSectionHeading}</legend>
              <p className="eg-admin__hint">{F.glanceSectionDescription}</p>
              <div className="eg-admin__form">
                <FormField id="eg-layout-glance-stats" label={F.glanceRowCapWithStats} description={F.glanceRowCapWithStatsHint}>
                  <input
                    type="number"
                    {...CAP_RANGE}
                    value={settings.glanceRowCapWithStats}
                    onChange={(event) => updateField({ glanceRowCapWithStats: Number(event.target.value) })}
                    {...formFieldControlProps("eg-layout-glance-stats", F.glanceRowCapWithStatsHint)}
                  />
                </FormField>
                <FormField id="eg-layout-glance-plain" label={F.glanceRowCapPlain} description={F.glanceRowCapPlainHint}>
                  <input
                    type="number"
                    {...CAP_RANGE}
                    value={settings.glanceRowCapPlain}
                    onChange={(event) => updateField({ glanceRowCapPlain: Number(event.target.value) })}
                    {...formFieldControlProps("eg-layout-glance-plain", F.glanceRowCapPlainHint)}
                  />
                </FormField>
              </div>
            </fieldset>
            <fieldset className="eg-admin__fieldset">
              <legend>{F.detailSectionHeading}</legend>
              <p className="eg-admin__hint">{F.detailSectionDescription}</p>
              <div className="eg-admin__form">
                <FormField id="eg-layout-risk-decision" label={F.riskDecisionItemsPerSlide} description={F.riskDecisionItemsPerSlideHint}>
                  <input
                    type="number"
                    {...CAP_RANGE}
                    value={settings.riskDecisionItemsPerSlide}
                    onChange={(event) => updateField({ riskDecisionItemsPerSlide: Number(event.target.value) })}
                    {...formFieldControlProps("eg-layout-risk-decision", F.riskDecisionItemsPerSlideHint)}
                  />
                </FormField>
                <FormField id="eg-layout-requests" label={F.requestsPerSlide} description={F.requestsPerSlideHint}>
                  <input
                    type="number"
                    {...CAP_RANGE}
                    value={settings.requestsPerSlide}
                    onChange={(event) => updateField({ requestsPerSlide: Number(event.target.value) })}
                    {...formFieldControlProps("eg-layout-requests", F.requestsPerSlideHint)}
                  />
                </FormField>
              </div>
            </fieldset>
            <div className="eg-admin__actions">
              <button type="submit" className="eg-admin__btn--primary" disabled={saving}>
                {saving ? F.saving : F.save}
              </button>
            </div>
          </form>

          {/* Read-only summary of the quarter policy (D-245's placeholder/lifecycle columns among
              it). Column policy is configuration this screen deliberately does NOT edit - the form
              above round-trips quarterPolicy unchanged - so this card only states what is loaded. */}
          <div className="eg-admin-layout__board-columns">
            <h3>{FB.heading}</h3>
            <p className="eg-admin__hint">{FB.note}</p>
            <dl>
              <div>
                <dt>{FB.forwardColumns}</dt>
                <dd>{settings.quarterPolicy.forwardColumnCount}</dd>
              </div>
              <div>
                <dt>{FB.catchAll}</dt>
                <dd>{settings.quarterPolicy.includeCatchAll ? settings.quarterPolicy.catchAllLabel : FB.catchAllOff}</dd>
              </div>
              <div>
                <dt>{FB.placeholders}</dt>
                <dd>
                  {settings.quarterPolicy.placeholderColumns.length > 0
                    ? settings.quarterPolicy.placeholderColumns.map((column) => column.label).join(", ")
                    : FB.none}
                </dd>
              </div>
              <div>
                <dt>{FB.lifecycle}</dt>
                <dd>
                  {settings.quarterPolicy.lifecycleColumns.length > 0
                    ? settings.quarterPolicy.lifecycleColumns.map((column) => column.label).join(", ")
                    : FB.none}
                </dd>
              </div>
            </dl>
          </div>
        </>
      )}
    </section>
  );
}
